// =============================================================================
// loginCore - pure helpers for the sign-in screen (src/auth/LoginScreen.jsx).
// No React and no Supabase so node:test can import it directly.
//
//   friendlyAuthError(raw, online)  server / network error -> English key
//   rememberedEmail() / rememberEmail(email, on)
//                                   "Remember my email on this device"
//   resendSecondsLeft(sentAt, now)  countdown before "Send a new code"
// =============================================================================

// English keys; LoginScreen translates them at render (tk / t).
export const AUTH_ERRORS = {
  credentials: "That email and password don't match. Check them, or use Forgot password.",
  unconfirmed: "Your account isn't set up yet. Open the invite email from your practice to finish.",
  rateLimited: "Too many tries. Wait a few minutes, then try again.",
  offline: "You appear to be offline. Check your connection and try again.",
  generic: "Sign in failed. Please try again.",
};

// Maps Supabase / fetch errors to a plain sentence. Unknown server text falls
// back to the generic sentence rather than showing raw error text.
export function friendlyAuthError(raw, online = true) {
  const msg = String((raw && (raw.message || raw)) || "");
  if (online === false || /failed to fetch|networkerror|network request failed|load failed/i.test(msg)) return AUTH_ERRORS.offline;
  if (/invalid login credentials|invalid (email|password)|invalid_grant/i.test(msg)) return AUTH_ERRORS.credentials;
  if (/email not confirmed|not confirmed/i.test(msg)) return AUTH_ERRORS.unconfirmed;
  if (/too many|rate limit|429|over_request_rate/i.test(msg)) return AUTH_ERRORS.rateLimited;
  return AUTH_ERRORS.generic;
}

// Email only (never the password), and only after the user opts in. Kept off
// by default so a shared front-desk computer does not keep anyone's email.
export const EMAIL_KEY = "practiceos.login_email";

export function rememberedEmail(storage) {
  try {
    const s = storage || (typeof window !== "undefined" ? window.localStorage : null);
    const v = s ? s.getItem(EMAIL_KEY) : null;
    return v && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v) ? v : "";
  } catch { return ""; }
}

export function rememberEmail(email, on, storage) {
  try {
    const s = storage || (typeof window !== "undefined" ? window.localStorage : null);
    if (!s) return;
    const v = String(email || "").trim();
    if (on && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) s.setItem(EMAIL_KEY, v);
    else s.removeItem(EMAIL_KEY);
  } catch { /* storage blocked */ }
}

// Seconds until another code may be requested (0 = now).
export const RESEND_WAIT_S = 30;
export function resendSecondsLeft(sentAt, now = Date.now(), waitS = RESEND_WAIT_S) {
  if (!sentAt) return 0;
  return Math.max(0, Math.ceil((sentAt + waitS * 1000 - now) / 1000));
}

// Smoke journeys shorten the wait with window.__POS_RESEND_WAIT_S (test hook).
export function resendWaitS() {
  const v = typeof window !== "undefined" ? Number(window.__POS_RESEND_WAIT_S) : NaN;
  return v > 0 && v < RESEND_WAIT_S ? v : RESEND_WAIT_S;
}
